import { ReactNode } from 'react';
import { Box, useMantineTheme, useMantineColorScheme } from '@mantine/core';
import { useReducedMotion } from '@/components/animations';

interface MainBackgroundProps {
  children: ReactNode;
}

export default function MainBackground({ children }: MainBackgroundProps) {
  const theme = useMantineTheme();
  const { colorScheme } = useMantineColorScheme();
  const reduceMotion = useReducedMotion();
  const isDark = colorScheme === 'dark';

  // Professional gradients - different from auth but similar style
  const gradientColors = {
    light: { from: theme.colors.gray[0], to: theme.colors.blue[0] },
    dark: { from: theme.colors.dark[9], to: theme.colors.dark[8] }
  };

  const currentGradient = isDark ? gradientColors.dark : gradientColors.light;

  return (
    <Box
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        width: '100vw',
        height: '100vh',
        background: `linear-gradient(135deg, ${currentGradient.from} 0%, ${currentGradient.to} 100%)`,
        overflow: 'auto',
      }}
    >
      {/* Animated background elements */}
      <Box
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          pointerEvents: 'none',
        }}
      >
        {/* Floating shapes - different positions and sizes than auth */}
        {[...Array(4)].map((_, i) => (
          <Box
            key={i}
            style={{
              position: 'absolute',
              width: 120 + i * 60,
              height: 120 + i * 60,
              borderRadius: '50%',
              background: `linear-gradient(135deg, ${theme.colors.blue[isDark ? 7 : 3]}, ${theme.colors.violet[isDark ? 7 : 3]})`,
              opacity: isDark ? 0.06 : 0.09,
              top: `${15 + i * 20}%`,
              right: `${10 + i * 15}%`,
              animation: reduceMotion ? 'none' : `drift ${6 + i * 2}s ease-in-out infinite`,
              animationDelay: `${i * 0.7}s`,
            }}
          />
        ))}

        {/* Additional accent shapes */}
        <Box
          style={{
            position: 'absolute',
            width: 180,
            height: 180,
            borderRadius: '50%',
            background: `linear-gradient(135deg, ${theme.colors.cyan[isDark ? 7 : 3]}, ${theme.colors.teal[isDark ? 7 : 3]})`,
            opacity: isDark ? 0.05 : 0.08,
            bottom: '20%',
            left: '15%',
            animation: reduceMotion ? 'none' : 'pulse 8s ease-in-out infinite',
          }}
        />
      </Box>

      {/* Content */}
      <Box style={{ position: 'relative', zIndex: 1, minHeight: '100vh' }}>
        {children}
      </Box>

      {/* Keyframes for animations */}
      <style>{`
        @keyframes drift {
          0%, 100% {
            transform: translate(0, 0) rotate(0deg);
          }
          33% {
            transform: translate(-15px, -10px) rotate(3deg);
          }
          66% {
            transform: translate(10px, -15px) rotate(-2deg);
          }
        }

        @keyframes pulse {
          0%, 100% {
            transform: scale(1);
            opacity: ${isDark ? 0.01 : 0.03};
          }
          50% {
            transform: scale(1.05);
            opacity: ${isDark ? 0.05 : 0.03};
          }
        }
      `}</style>
    </Box>
  );
}
